/**
 * Platform API — Tenants resource.
 *
 * Thin typed wrappers around axios. Consumers should prefer the TanStack
 * Query hooks below — calling these functions directly is only appropriate in
 * tests and in migration scripts.
 *
 * Endpoint assumptions (see BRD §7.1 FR-MT-01..07 and UI Spec §11.3/11.4):
 *   - GET    /v1/platform/tenants                        (list, paginated + filtered)
 *   - GET    /v1/platform/tenants/{id}                   (detail)
 *   - GET    /v1/platform/tenants/check-short-code       (availability)
 *   - POST   /v1/platform/tenants                        (create Draft)
 *   - POST   /v1/platform/tenants/{id}:activate          (promote Draft → Active)
 *   - PATCH  /v1/platform/tenants/{id}                   (edit)
 *   - POST   /v1/platform/tenants/{id}:suspend           (Active → Suspended)
 *   - POST   /v1/platform/tenants/{id}:activate          (Suspended → Active)
 *   - POST   /v1/platform/tenants/{id}:archive           (any → Archived)
 *   - POST   /v1/platform/tenants/{id}/impersonation     (begin session)
 *   - DELETE /v1/platform/impersonation/sessions/current (end current session)
 *
 * These paths are UNCONFIRMED against the Platform API scaffold — if the backend
 * picks different URIs, change them here in one place.
 */
import { apiClient } from '@/api/client'
import type { ServiceStatus } from '@/api/platform/health'

export type TenantStatus = 'draft' | 'active' | 'suspended' | 'archived'
export type TenantPlan = 'starter' | 'growth' | 'scale' | 'enterprise'

export interface TenantSummary {
  id: string
  name: string
  shortCode: string
  logoUrl?: string | null
  status: TenantStatus
  plan: TenantPlan
  activeStudents: number
  monthlyRecurringRevenue: number
  currency: string
  createdAt: string
  lastActiveAt?: string | null
}

export interface TenantDetail extends TenantSummary {
  legalName: string
  legalEntity?: string
  country: string
  timezone: string
  contactPerson: {
    name: string
    email: string
    phone: string
  }
  billingStartDate?: string
  invoiceContact?: {
    name: string
    email: string
  }
  updatedAt: string
}

export interface TenantListFilters {
  statuses?: TenantStatus[]
  plans?: TenantPlan[]
  createdFrom?: string
  createdTo?: string
  studentCountMin?: number
  studentCountMax?: number
  search?: string
  page?: number
  pageSize?: number
}

export interface Paginated<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export interface CheckShortCodeResponse {
  available: boolean
  suggestion?: string
}

/**
 * `POST /v1/platform/tenants` payload — matches the Platform API's
 * `CreateTenantRequest` DTO exactly.
 *
 * `shortCode` regex on the backend: `^[a-z][a-z0-9-]{2,31}$` — must start
 * with a lowercase letter and is 3–32 characters long. The wizard validates
 * the same pattern client-side before calling.
 */
export interface CreateTenantPayload {
  shortCode: string
  legalName: string
  /** Optional — the "display name" shown to staff and parents. */
  tradingName?: string
  physicalAddress: string
  contactName: string
  contactEmail: string
  contactPhone: string
  /** Optional — S3 key for a logo uploaded out-of-band. */
  logoS3Key?: string
}

/**
 * `POST /v1/platform/tenants/{id}/activate` payload — matches the Platform
 * API's `ActivateTenantRequest` DTO.
 *
 * The Super Admin designates the initial School Admin (name + email + phone);
 * no password is set here — the School Admin activates their own account via
 * the one-shot token the server dispatches out-of-band (FR-MT-05).
 *
 * Plan selection, billing start date, and invoice contact are collected in
 * the onboarding wizard but are NOT part of this call — they'll flow into
 * the dedicated plan assignment + billing endpoints in a follow-up PR. For
 * now only `planId` is forwarded; other fields are stored in the wizard's
 * state for UI review only.
 */
export interface ActivateTenantPayload {
  schoolAdminFullName: string
  schoolAdminEmail: string
  schoolAdminPhone: string
  /** Optional — assign later via the plan endpoint if omitted. */
  planId?: string
}

export interface ReasonPayload {
  reason: string
}

export interface ImpersonationSession {
  sessionId: string
  /**
   * Scoped bearer token the SPA swaps into the auth header while the session
   * is live. Only populated on the `POST /impersonation` start response —
   * `GET /sessions/current` deliberately omits the token (the SPA already
   * has the running token in memory; we don't re-issue on hydrate).
   */
  accessToken?: string
  tenantId: string
  tenantName: string
  expiresAt: string
  startedAt: string
  impersonatedUserId?: string
  impersonatedUserName?: string
  reason?: string
}

export interface TenantAdminUser {
  id: string
  name: string
  email: string
  phone?: string
  roles: string[]
  lastLoginAt?: string | null
  status: 'active' | 'invited' | 'suspended'
}

export interface TenantIntegrationHealth {
  integration: 'mpesa' | 'card' | 'sms' | 'email' | 'biometric'
  status: 'healthy' | 'degraded' | 'down'
  lastHeartbeatAt?: string
  message?: string
}

export interface TenantActivityEvent {
  id: string
  at: string
  actor: string
  action: string
  summary: string
}

export interface TenantKpis {
  activeStudents: number
  mrr: number
  transactionsToday: number
  feeCollectionRate: number
  currency: string
}

// ---------------------------------------------------------------------------
// List / detail
// ---------------------------------------------------------------------------

export async function listTenants(filters: TenantListFilters = {}): Promise<Paginated<TenantSummary>> {
  const { data } = await apiClient.get<Paginated<TenantSummary>>('/v1/platform/tenants', {
    params: serializeFilters(filters),
  })
  return data
}

export async function getTenant(id: string): Promise<TenantDetail> {
  const { data } = await apiClient.get<TenantDetail>(`/v1/platform/tenants/${id}`)
  return data
}

export async function getTenantKpis(id: string): Promise<TenantKpis> {
  const { data } = await apiClient.get<TenantKpis>(`/v1/platform/tenants/${id}/kpis`)
  return data
}

export async function getTenantActivity(id: string): Promise<TenantActivityEvent[]> {
  const { data } = await apiClient.get<TenantActivityEvent[]>(`/v1/platform/tenants/${id}/activity`)
  return data
}

export async function getTenantAdmins(id: string): Promise<TenantAdminUser[]> {
  const { data } = await apiClient.get<TenantAdminUser[]>(`/v1/platform/tenants/${id}/users`)
  return data
}

export async function getTenantIntegrations(id: string): Promise<TenantIntegrationHealth[]> {
  const { data } = await apiClient.get<TenantIntegrationHealth[]>(
    `/v1/platform/tenants/${id}/integrations`,
  )
  return data
}

// ---------------------------------------------------------------------------
// Mutations — lifecycle
// ---------------------------------------------------------------------------

export async function checkShortCode(code: string): Promise<CheckShortCodeResponse> {
  const { data } = await apiClient.get<CheckShortCodeResponse>(
    '/v1/platform/tenants/check-short-code',
    { params: { code } },
  )
  return data
}

export async function createTenant(payload: CreateTenantPayload): Promise<TenantDetail> {
  const { data } = await apiClient.post<TenantDetail>('/v1/platform/tenants', payload)
  return data
}

/**
 * Server response from `POST /v1/platform/tenants/{id}/activate` — matches
 * the backend `ActivationResponse` record.
 *
 * `activationToken` + `activationUrl` are returned ONCE so the Super Admin
 * can forward them if the notification channel fails — the server only
 * persists the SHA-256 hash (FR-MT-05).
 */
export interface ActivationResponse {
  tenantId: string
  schoolAdminUserId: string
  activationToken: string
  activationUrl: string
}

/**
 * Promote a DRAFT tenant to ACTIVE and seed the initial School Admin user.
 * The server dispatches an activation email + SMS to the admin (FR-MT-05).
 */
export async function activateTenant(
  id: string,
  payload: ActivateTenantPayload,
): Promise<ActivationResponse> {
  const { data } = await apiClient.post<ActivationResponse>(
    `/v1/platform/tenants/${id}/activate`,
    payload,
  )
  return data
}

/**
 * Suspend / reactivate / archive all share the single transitions endpoint
 * (`POST /v1/platform/tenants/{id}/transitions`). The body carries the
 * target state and a reason — the backend enforces the transition matrix
 * and writes the audit trail.
 */
async function transitionTenant(
  id: string,
  target: 'DRAFT' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED',
  reason: string,
): Promise<TenantDetail> {
  const { data } = await apiClient.post<TenantDetail>(
    `/v1/platform/tenants/${id}/transitions`,
    { target, reason },
  )
  return data
}

export async function suspendTenant(id: string, payload: ReasonPayload): Promise<TenantDetail> {
  return transitionTenant(id, 'SUSPENDED', payload.reason)
}

export async function reactivateTenant(id: string, payload: ReasonPayload): Promise<TenantDetail> {
  return transitionTenant(id, 'ACTIVE', payload.reason)
}

export async function archiveTenant(id: string, payload: ReasonPayload): Promise<TenantDetail> {
  return transitionTenant(id, 'ARCHIVED', payload.reason)
}

export async function bulkUpdateStatus(
  ids: string[],
  status: TenantStatus,
  reason: string,
): Promise<{ updated: number; failed: number; firstError?: string }> {
  // Bulk endpoint isn't implemented on the backend yet — map to the single
  // transitions endpoint per tenant until a true bulk operation ships.
  // Per-tenant failures used to be silently swallowed; surfacing the first
  // error makes "Updated 0 schools" actionable (typical cause: trying to
  // promote a DRAFT tenant — the backend reserves DRAFT→ACTIVE for the
  // dedicated /activate endpoint that seeds the initial School Admin).
  const target = status.toUpperCase() as 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED' | 'DRAFT'
  let updated = 0
  let firstError: string | undefined
  for (const id of ids) {
    try {
      await transitionTenant(id, target, reason)
      updated += 1
    } catch (err) {
      if (!firstError) {
        const e = err as { message?: string; response?: { data?: { message?: string } } }
        firstError = e.response?.data?.message ?? e.message ?? 'Status change rejected.'
      }
    }
  }
  return { updated, failed: ids.length - updated, firstError }
}

// ---------------------------------------------------------------------------
// Impersonation
// ---------------------------------------------------------------------------

export async function startImpersonation(
  tenantId: string,
  reason: string,
): Promise<ImpersonationSession> {
  const { data } = await apiClient.post<ImpersonationSession>(
    `/v1/platform/tenants/${tenantId}/impersonation`,
    { reason },
  )
  return data
}

export async function endCurrentImpersonation(): Promise<void> {
  await apiClient.delete('/v1/platform/impersonation/sessions/current')
}

/**
 * Fetch the caller's active impersonation session. Returns `null` when the
 * server replies 204 (no active session) so the store can treat absence as a
 * first-class value rather than a thrown error.
 */
export async function getCurrentImpersonationSession(): Promise<ImpersonationSession | null> {
  const response = await apiClient.get<ImpersonationSession | ''>(
    '/v1/platform/impersonation/sessions/current',
    { validateStatus: (status) => status === 200 || status === 204 },
  )
  if (response.status === 204 || !response.data) return null
  return response.data as ImpersonationSession
}

// ---------------------------------------------------------------------------
// Tenant detail sub-tabs — S-SUP-040
// (overview KPIs, paginated users, integration health, platform invoices,
//  tenant-scoped notes). Wired into apps/admin-web/src/pages/platform/
//  tenant-detail/*.vue.
// ---------------------------------------------------------------------------

/**
 * Envelope for monetary values returned by the Platform API. Mirrors the
 * backend `Money` record — minor units (e.g. cents) plus an ISO-4217 code so
 * the UI can format with `Intl.NumberFormat`.
 */
export interface Money {
  minorUnits: number
  currency: string
}

export interface TenantOverview {
  activeStudents: number
  outstandingFees: Money
  walletFloat: Money
  planName?: string | null
  planEffectiveFrom?: string | null
}

export interface TenantUserSummary {
  id: string
  email: string
  displayName: string
  roles: string[]
  lastSignInAt?: string | null
  status: 'active' | 'pending_activation' | 'suspended' | 'disabled'
}

/**
 * Per-tenant health readout for a single integration. `maskedCredential`,
 * when present, is guaranteed by the backend to be the last 4 characters of
 * the raw value prefixed with the masking glyph — the UI never receives the
 * plaintext credential.
 */
export interface IntegrationHealth {
  configured: boolean
  status: ServiceStatus
  maskedCredential?: string | null
  lastSuccessfulCallAt?: string | null
}

export interface TenantIntegrations {
  mpesa: IntegrationHealth
  card: IntegrationHealth
  sms: IntegrationHealth
  email: IntegrationHealth
}

export interface PlatformInvoiceSummary {
  id: string
  number: string
  issuedOn: string
  periodStart: string
  periodEnd: string
  total: Money
  status: 'draft' | 'issued' | 'paid' | 'overdue' | 'void'
}

export interface TenantNote {
  id: string
  body: string
  authorUserId: string
  authorDisplayName: string
  createdAt: string
  updatedAt: string
}

export interface CreateTenantNotePayload {
  body: string
}

export async function getTenantOverview(id: string): Promise<TenantOverview> {
  const { data } = await apiClient.get<TenantOverview>(
    `/v1/platform/tenants/${id}/overview`,
  )
  return data
}

export async function listTenantUsers(
  id: string,
  page = 0,
  pageSize = 50,
): Promise<Paginated<TenantUserSummary>> {
  const { data } = await apiClient.get<Paginated<TenantUserSummary>>(
    `/v1/platform/tenants/${id}/users`,
    { params: { page, pageSize } },
  )
  return data
}

export async function getTenantIntegrationHealth(id: string): Promise<TenantIntegrations> {
  const { data } = await apiClient.get<TenantIntegrations>(
    `/v1/platform/tenants/${id}/integrations`,
  )
  return data
}

/**
 * Platform-to-tenant invoices. The billing service lands in Phase 1B; until
 * then the backend returns an empty `Paginated<T>` envelope so the UI can
 * render the zero-state without a dedicated stub.
 */
export async function listTenantPlatformInvoices(
  id: string,
  page = 0,
  pageSize = 20,
): Promise<Paginated<PlatformInvoiceSummary>> {
  const { data } = await apiClient.get<Paginated<PlatformInvoiceSummary>>(
    `/v1/platform/tenants/${id}/billing/invoices`,
    { params: { page, pageSize } },
  )
  return data
}

export async function listTenantNotes(
  id: string,
  page = 0,
  pageSize = 50,
): Promise<Paginated<TenantNote>> {
  const { data } = await apiClient.get<Paginated<TenantNote>>(
    `/v1/platform/tenants/${id}/notes`,
    { params: { page, pageSize } },
  )
  return data
}

export async function createTenantNote(
  id: string,
  payload: CreateTenantNotePayload,
): Promise<TenantNote> {
  const { data } = await apiClient.post<TenantNote>(
    `/v1/platform/tenants/${id}/notes`,
    payload,
  )
  return data
}

export async function deleteTenantNote(id: string, noteId: string): Promise<void> {
  await apiClient.delete(`/v1/platform/tenants/${id}/notes/${noteId}`)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function serializeFilters(f: TenantListFilters): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  if (f.statuses?.length) out.status = f.statuses.join(',')
  if (f.plans?.length) out.plan = f.plans.join(',')
  if (f.createdFrom) out.createdFrom = f.createdFrom
  if (f.createdTo) out.createdTo = f.createdTo
  if (f.studentCountMin != null) out.studentCountMin = f.studentCountMin
  if (f.studentCountMax != null) out.studentCountMax = f.studentCountMax
  if (f.search) out.q = f.search
  if (f.page) out.page = f.page
  if (f.pageSize) out.pageSize = f.pageSize
  return out
}
